import React, { useEffect, useMemo, useRef, useState } from 'react'
import apiService from '../../services/api.service'

// Agendamento das telas fixas de relatorio (PHOENIX-106, fase 2). Os filtros gravados sao o que a tela manda para o
// endpoint dela ("Visualizar"); a edicao no dialogo reaproveita o formulario da propria tela, passado por parametro.

const PATH_TIPOS_SEPULTURAS = '/lottustech/relatorios/tipossepulturas/'

const preenchido = (valor) => valor !== undefined && valor !== null

/** Lista da tela ({ id, label }, ou { id, value } quando vem da unidade do topo) -> { id, value }, que o back recebe. */
export const paraIdValue = (lista) =>
  Array.isArray(lista)
    ? lista.map((item) => ({ id: item.id, value: preenchido(item.value) ? item.value : item.label }))
    : []

/** { id, value } gravado -> { id, label }, que os seletores da tela usam. */
export const paraIdLabel = (lista) =>
  Array.isArray(lista)
    ? lista.map((item) => ({ id: item.id, label: preenchido(item.label) ? item.label : item.value }))
    : []

/** Os ids de uma lista da tela ({ id, ... }). */
const idsDe = (lista) => (Array.isArray(lista) ? lista.map((item) => item.id) : [])

/**
 * Conversao entre o registro da tela e os filtros do agendamento.
 *
 * @param listas     { campoDaTela: campoDoFiltro } dos seletores de lista (ex.: { unidade: 'unidades' })
 * @param simples    campos que passam como estao, com o mesmo nome (datas, numeros, textos)
 * @param renomeados { campoDaTela: campoDoFiltro } dos campos simples que o back recebe com outro nome
 * @param ids        { campoDaTela: { filtro, opcoes } } dos seletores que o back recebe so como lista de ids (ex.:
 *                   forma de pagamento). opcoes(carregadas) devolve as opcoes ({ id, value }), para mostrar o nome
 *                   do que esta gravado; recebe as listas que o editor carregou (outrasListas), para as opcoes que
 *                   vem do back
 */
export const criarConversor = ({ listas = {}, simples = [], renomeados = {}, ids = {} }) => {
  const campoDaTela = {}
  Object.keys(listas).forEach((tela) => {
    campoDaTela[listas[tela]] = tela
  })
  Object.keys(renomeados).forEach((tela) => {
    campoDaTela[renomeados[tela]] = tela
  })
  Object.keys(ids).forEach((tela) => {
    campoDaTela[ids[tela].filtro] = tela
  })
  const camposSimples = simples.map((campo) => [campo, campo]).concat(Object.entries(renomeados))
  return {
    /** O que o "Visualizar" da tela mandaria, a partir do registro dela. */
    filtrosDoRecord: (record = {}) => {
      const filtros = {}
      Object.keys(listas).forEach((tela) => {
        filtros[listas[tela]] = paraIdValue(record[tela])
      })
      Object.keys(ids).forEach((tela) => {
        filtros[ids[tela].filtro] = idsDe(record[tela])
      })
      camposSimples.forEach(([tela, filtro]) => {
        if (preenchido(record[tela]) && record[tela] !== '') {
          filtros[filtro] = record[tela]
        }
      })
      return filtros
    },
    /**
     * O registro que o formulario da tela espera, a partir dos filtros gravados. carregadas: as listas de opcoes que
     * o editor carregou, por campo do registro.
     */
    recordDosFiltros: (filtros = {}, carregadas = {}) => {
      const record = {}
      Object.keys(listas).forEach((tela) => {
        record[tela] = paraIdLabel(filtros[listas[tela]])
      })
      Object.keys(ids).forEach((tela) => {
        const opcoes = ids[tela].opcoes(carregadas) || []
        const gravados = Array.isArray(filtros[ids[tela].filtro]) ? filtros[ids[tela].filtro] : []
        record[tela] = gravados.map((id) => {
          const opcao = opcoes.find((item) => item.id === id)
          return { id, label: opcao ? opcao.value : String(id) }
        })
      })
      camposSimples.forEach(([tela, filtro]) => {
        record[tela] = filtros[filtro]
      })
      return record
    },
    /** Mudanca de um campo do formulario -> [campo do filtro, valor]. */
    filtroDoCampo: (campo, valor) => {
      if (listas[campo]) {
        return [listas[campo], paraIdValue(valor)]
      }
      if (ids[campo]) {
        return [ids[campo].filtro, idsDe(valor)]
      }
      return [renomeados[campo] || campo, valor]
    },
    campoDaTela: (campoDoFiltro) => campoDaTela[campoDoFiltro] || campoDoFiltro,
  }
}

/** Dos tipos marcados, os que ainda existem nas opcoes (a tela faz o mesmo ao trocar a unidade). */
export const tiposAindaValidos = (marcados, opcoes) => {
  const nomes = new Set((opcoes || []).map((opcao) => opcao.value))
  return (marcados || []).filter((tipo) => nomes.has(tipo.value))
}

/**
 * As opcoes do seletor: as carregadas mais os itens ja marcados que nao vieram nelas. Com uma unidade escolhida no
 * topo da tela o back so devolve os itens dela, e sem isto o que esta salvo para outras unidades apareceria sem nome.
 *
 * @param chave o que identifica o item: 'value' nos tipos de sepultura (a tela os compara pelo nome, o mesmo tipo
 *              tem um id por unidade) e 'id' nas demais listas
 */
export const opcoesComMarcados = (opcoes, marcados, chave = 'value') => {
  const presentes = new Set((opcoes || []).map((opcao) => opcao[chave]))
  return (opcoes || []).concat((marcados || []).filter((item) => !presentes.has(item[chave])))
}

/** O mesmo para o seletor gravado so com os ids: o id marcado que nao veio nas opcoes entra com o proprio id no nome. */
export const opcoesComIdsMarcados = (opcoes, idsMarcados) =>
  opcoesComMarcados(
    opcoes,
    (Array.isArray(idsMarcados) ? idsMarcados : []).map((id) => ({ id, value: String(id) })),
    'id'
  )

/**
 * Editor de filtros do AgendarRelatorioDialog para as telas fixas de relatorio: o
 * formulario da propria tela, sem as datas (que vao para a grade de formulas). Carrega os tipos de sepultura das
 * unidades escolhidas, o que na tela e feito pelo container.
 *
 * @param Form      o componente de formulario da tela (recebe record, changeHandler, onChangeUnit, noAgendamento)
 * @param conversor ver criarConversor
 * @param opcoes    tiposSepulturas: false para a tela que nao tem o filtro de tipo de sepultura;
 *                  outrasListas: [{ campo, filtro | filtroIds, carregar(unidades) }] das demais listas de opcoes
 *                  que o container da tela carrega pelas unidades escolhidas (campo: onde o formulario as le no
 *                  record; filtro: o filtro de lista { id, value } que elas alimentam, ou filtroIds: o filtro gravado
 *                  so com os ids; carregar devolve uma Promise com as opcoes { id, value });
 *                  propsForm(): o que mais o formulario recebe do container da tela, lido uma vez;
 *                  propsDosFiltros(filtros, onChange, extras): o que o formulario recebe do container e depende dos
 *                  filtros (estado e manipuladores que na tela ficam fora do record)
 */
export const criarEditorTelaFixa = (
  Form,
  conversor,
  {
    tiposSepulturas: comTiposSepulturas = true,
    outrasListas = [],
    propsForm = () => ({}),
    propsDosFiltros = () => ({}),
  } = {}
) => {
  const EditorFiltrosTelaFixa = ({ filtros, onChange }) => {
    const [opcoesTiposSepulturas, setOpcoesTiposSepulturas] = useState(null)
    const [opcoesOutrasListas, setOpcoesOutrasListas] = useState({})
    // so depois de o usuario trocar a unidade no dialogo: ao abrir, o que esta salvo nao e mexido
    const unidadeAlterada = useRef(false)
    const unidades = filtros.unidades || []
    const chaveUnidades = JSON.stringify(unidades.map((unidade) => unidade.id))
    // lidos do storage (dicionario, configuracao da empresa): uma vez por dialogo aberto
    const extrasDoForm = useMemo(propsForm, [])

    useEffect(() => {
      if (!comTiposSepulturas) {
        return undefined
      }
      let montado = true
      apiService
        .post(PATH_TIPOS_SEPULTURAS, { unidades })
        .then((resposta) => montado && setOpcoesTiposSepulturas(resposta.data || []))
        .catch(() => montado && setOpcoesTiposSepulturas([]))
      return () => {
        montado = false
      }
    }, [chaveUnidades]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      let montado = true
      outrasListas.forEach(({ campo, carregar }) => {
        carregar(unidades)
          .then((lista) => montado && setOpcoesOutrasListas((atuais) => ({ ...atuais, [campo]: lista || [] })))
          .catch(() => montado && setOpcoesOutrasListas((atuais) => ({ ...atuais, [campo]: [] })))
      })
      return () => {
        montado = false
      }
    }, [chaveUnidades]) // eslint-disable-line react-hooks/exhaustive-deps

    // trocada a unidade, o tipo marcado que nao existe nas unidades novas sai do filtro, como na tela. Ao abrir nao:
    // com uma unidade escolhida no topo o back so devolve os tipos dela, e isso apagaria o que esta salvo
    useEffect(() => {
      if (!unidadeAlterada.current || !opcoesTiposSepulturas || !Array.isArray(filtros.tiposSepulturas)) {
        return
      }
      unidadeAlterada.current = false
      const validos = tiposAindaValidos(filtros.tiposSepulturas, opcoesTiposSepulturas)
      if (validos.length !== filtros.tiposSepulturas.length) {
        onChange('tiposSepulturas', validos)
      }
    }, [opcoesTiposSepulturas]) // eslint-disable-line react-hooks/exhaustive-deps

    const alterar = (evento) => {
      const alvo = evento.target
      // caixa de marcar: o valor e o "checked", como no changeHandler das telas
      const valorDoCampo = alvo.type === 'checkbox' ? alvo.checked : alvo.value
      const [campo, valor] = conversor.filtroDoCampo(alvo.name, valorDoCampo)
      if (campo === 'unidades') {
        unidadeAlterada.current = true
      }
      onChange(campo, valor)
    }

    const record = {
      ...conversor.recordDosFiltros(filtros, opcoesOutrasListas),
      opcoesTiposSepulturas: opcoesComMarcados(opcoesTiposSepulturas, filtros.tiposSepulturas),
    }
    // o que esta marcado entra sempre nas opcoes, pelo mesmo motivo dos tipos de sepultura (unidade do topo)
    outrasListas.forEach(({ campo, filtro, filtroIds }) => {
      const carregadas = opcoesOutrasListas[campo]
      if (!carregadas) {
        return // ainda carregando: o seletor fica sem opcoes, e o que esta marcado continua no filtro
      }
      record[campo] = filtroIds
        ? opcoesComIdsMarcados(carregadas, filtros[filtroIds])
        : opcoesComMarcados(carregadas, filtros[filtro], 'id')
    })
    return (
      <div className="p-col-12 agendar-relatorio-filtros-tela">
        <Form
          {...extrasDoForm}
          {...propsDosFiltros(filtros, onChange, extrasDoForm)}
          record={record}
          changeHandler={alterar}
          onChangeUnit={alterar}
          noAgendamento={true}
        />
      </div>
    )
  }
  return EditorFiltrosTelaFixa
}
